<template>
    <div>
        <!-- Input de selección (oculto en solo lectura) -->
        <div v-if="!soloLectura" class="mb-2">
            <b-form-file
                ref="fileInput"
                v-model="archivosModel"
                :accept="aceptar"
                :multiple="multiple"
                placeholder="Seleccionar archivos..."
                browse-text="Examinar"
                @change="onSeleccionar"
            />
            <small class="text-muted">
                Formatos: imágenes y PDF. Máximo {{ maxArchivos }} archivo(s).
            </small>
        </div>

        <!-- Lista de archivos seleccionados (pendientes de subir) -->
        <div v-if="archivosSeleccionados.length && !soloLectura" class="mb-2">
            <p class="mb-1 font-weight-bold small text-secondary">Por subir:</p>
            <b-list-group flush>
                <b-list-group-item
                    v-for="(f, i) in archivosSeleccionados"
                    :key="'sel-' + i"
                    class="d-flex justify-content-between align-items-center py-1 px-2"
                    style="font-size:0.85rem"
                >
                    <span><b-icon icon="paperclip" class="mr-1" />{{ f.name }}</span>
                    <b-button variant="link" size="sm" class="text-danger p-0" @click="eliminarSeleccionado(i)">
                        <b-icon icon="x-circle" />
                    </b-button>
                </b-list-group-item>
            </b-list-group>
        </div>

        <!-- Archivos ya guardados -->
        <b-overlay :show="cargando" rounded="sm">
            <div v-if="archivosExistentes.length">
                <p class="mb-1 font-weight-bold small text-secondary">
                    {{ soloLectura ? 'Archivos adjuntos:' : 'Ya guardados:' }}
                </p>
                <b-list-group flush>
                    <b-list-group-item
                        v-for="(a, i) in archivosExistentes"
                        :key="'ex-' + i"
                        class="d-flex justify-content-between align-items-center py-1 px-2"
                        style="font-size:0.85rem; cursor:pointer;"
                        @click="verArchivo(a)"
                    >
                        <span>
                            <b-icon :icon="iconoTipo(a.typefile)" class="mr-1 text-primary" />
                            {{ a.name }}
                            <b-badge variant="light" class="border ml-1" style="font-size:0.75rem">{{ a.typefile }}</b-badge>
                        </span>
                        <b-icon icon="download" class="text-primary" />
                    </b-list-group-item>
                </b-list-group>
            </div>
            <p v-else-if="soloLectura && !cargando" class="text-muted small mb-0">Sin archivos adjuntos.</p>
        </b-overlay>

        <!-- Estado de subida -->
        <b-overlay :show="subiendo" rounded="sm" opacity="0.6">
            <template #overlay>
                <div class="text-center">
                    <b-spinner small variant="primary" />
                    <span class="ml-2 small">Subiendo archivos...</span>
                </div>
            </template>
            <div v-if="subiendo" style="height:2rem"></div>
        </b-overlay>
    </div>
</template>

<script>
import { RepositoryFactory } from "../repositories/RepositoryFactory";

const retiroRepo = RepositoryFactory.get("retiro");

export default {
    name: 'AdjuntosRetiro',
    props: {
        idRetiro:   { type: Number, default: null },
        soloLectura:{ type: Boolean, default: false },
        multiple:   { type: Boolean, default: true },
        aceptar:    { type: String,  default: 'image/*,application/pdf' },
        maxArchivos:{ type: Number,  default: 5 },
    },
    data() {
        return {
            archivosModel:       null,
            archivosSeleccionados: [],
            archivosExistentes:  [],
            cargando:  false,
            subiendo:  false,
        };
    },
    watch: {
        idRetiro(newId) {
            if (newId) this.cargarExistentes();
        },
    },
    mounted() {
        if (this.idRetiro) this.cargarExistentes();
    },
    methods: {
        onSeleccionar(event) {
            const files = Array.from(event.target.files || []);
            const disponible = this.maxArchivos - this.archivosSeleccionados.length;
            const nuevos = files.slice(0, disponible);
            this.archivosSeleccionados = [...this.archivosSeleccionados, ...nuevos];
            // Limpiar el input para permitir re-selección del mismo archivo
            this.$nextTick(() => { this.archivosModel = null; });
        },
        eliminarSeleccionado(idx) {
            this.archivosSeleccionados.splice(idx, 1);
        },

        // ── Llamar desde el padre tras crear/actualizar el retiro ──────────────
        async subirArchivos(idRetiro) {
            if (!this.archivosSeleccionados.length) return;
            this.subiendo = true;
            try {
                await retiroRepo.subirAdjuntos(idRetiro, this.archivosSeleccionados);
                this.archivosSeleccionados = [];
                await this.cargarExistentes(idRetiro);
            } catch (e) {
                console.error('[AdjuntosRetiro] Error al subir:', e);
            } finally {
                this.subiendo = false;
            }
        },

        async cargarExistentes(idOverride) {
            const id = idOverride || this.idRetiro;
            if (!id) return;
            this.cargando = true;
            try {
                const resp = await retiroRepo.consultarAdjuntos(id);
                this.archivosExistentes = Array.isArray(resp) ? resp : [];
            } catch (e) {
                this.archivosExistentes = [];
            } finally {
                this.cargando = false;
            }
        },

        verArchivo(archivo) {
            if (archivo.is_azure && archivo.temp_url) {
                window.open(archivo.temp_url, '_blank');
                return;
            }
            if (archivo.data && archivo.data.data) {
                const mime = this.mimeType(archivo.typefile);
                const blob = new Blob([new Uint8Array(archivo.data.data)], { type: mime });
                window.open(URL.createObjectURL(blob), '_blank');
            }
        },

        hayArchivos() {
            return this.archivosSeleccionados.length > 0;
        },

        iconoTipo(tipo) {
            if (!tipo) return 'paperclip';
            const t = tipo.toLowerCase();
            if (t.includes('pdf'))  return 'file-earmark-pdf';
            if (t.includes('png') || t.includes('jpg') || t.includes('jpeg')) return 'file-earmark-image';
            return 'file-earmark';
        },
        mimeType(tipo) {
            if (!tipo) return 'application/octet-stream';
            const t = tipo.toLowerCase();
            if (t.includes('pdf'))  return 'application/pdf';
            if (t.includes('png'))  return 'image/png';
            if (t.includes('jpg') || t.includes('jpeg')) return 'image/jpeg';
            return 'application/octet-stream';
        },
    },
};
</script>
